/**
 * useNotificationListener
 *
 * Phase 6e: DB-backed notification state via RTK Query cache.
 *
 * Three channels:
 * 1. **SW postMessage** — Web Push in foreground → toast + detail dialog
 * 2. **notification_deliveries CDC** — INSERT (new) + UPDATE (held→sent)
 *    → cache patch only on steady-state events. After a hidden→visible
 *    resubscribe (see §"Visibility-aware lifecycle" below), the bell's
 *    `NotificationHistory` cache tag is invalidated once so the bell
 *    catches up on anything that landed during the hidden window.
 *    UPDATE guard: only handle old.status != 'sent' AND new.status = 'sent'.
 * 3. **notification_send_queue CDC** — kept for admin banner compatibility
 *
 * **Visibility-aware lifecycle (#505):** when the tab has been hidden for
 * >60s, both channels are torn down — releases two `realtime.subscription`
 * rows on the server and stops the JWT-expiry log floods that come from
 * stale always-on bell connections. On hidden→visible the channels are
 * re-created via the same setup path; the `wokeUp` flag triggers the
 * one-shot `NotificationHistory` invalidation noted above.
 *
 * Unread count + history read from RTK cache (hydrated by dashboard bootstrap
 * on teacher routes, lazy-fetched on non-teacher routes).
 */
import { useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { supabase } from "@flexwise/shared/lib/supabaseClient";
import { useAuth } from "../../../contexts/AuthContext";
// Channel names are derived from profileId (stable per session) instead of
// Math.random() (regenerated on every mount). Stable names mean that if the
// hook ever does double-mount (StrictMode in dev, fast route flips), Supabase
// reuses the existing realtime.subscription row instead of opening a fresh
// one alongside the old.
import { notificationsApi } from "../api/notificationsApi";
import type {
  SWMessage,
  PushPayload,
  NotificationHistoryItem,
  NotificationHistoryPayload,
} from "../types/notificationTypes";

// ─── Constants ───────────────────────────────────────────────────────────────
const DEDUPE_TTL_MS = 60_000; // 60s window for client-side dedupe

/** Return a toast title appropriate for the event_type. Mirrors the bell
 *  list mapper in NotificationHistoryPanel — keep the two in sync when a new
 *  event_type lands so the toast doesn't fall through to "Neuer
 *  Vertretungsplan" for unrelated events. */
function toastTitle(
  eventType: string,
  isUpdate: boolean,
  payload?: Record<string, unknown> | null,
): string {
  const eventTitle =
    payload && typeof payload === "object"
      ? (payload as Record<string, unknown>).event_title
      : undefined;
  const mitteilungTitle =
    payload && typeof payload === "object"
      ? (payload as Record<string, unknown>).title
      : undefined;

  if (eventType === "calendar_invitation") {
    return typeof mitteilungTitle === "string" && mitteilungTitle
      ? `Kalender-Einladung: ${mitteilungTitle}`
      : "Kalender-Einladung";
  }
  if (eventType === "calendar_cancellation") {
    return typeof mitteilungTitle === "string" && mitteilungTitle
      ? `Termin abgesagt: ${mitteilungTitle}`
      : "Termin abgesagt";
  }
  // Begleit-teacher dropped from a Klassenfahrt anfrage by the approving
  // admin. Trip itself is approved, but THIS teacher isn't going.
  if (eventType === "class_trip_member_removed") {
    return typeof eventTitle === "string" && eventTitle
      ? `Aus Begleitung entfernt: ${eventTitle}`
      : "Aus Begleitung entfernt";
  }
  // Begleit-teacher just got added to a class-trip request. Pending vs
  // auto-approved framing matches the bell list mapper.
  if (eventType === "class_trip_member_invited") {
    const pending =
      payload && typeof payload === "object"
        ? (payload as Record<string, unknown>).approval_status === "pending"
        : false;
    const verb = pending ? "Begleitung angefragt" : "Als Begleitung eingetragen";
    return typeof eventTitle === "string" && eventTitle
      ? `${verb}: ${eventTitle}`
      : verb;
  }
  // Trip the teacher was on got admin-approved.
  if (eventType === "class_trip_member_approved") {
    return typeof eventTitle === "string" && eventTitle
      ? `Begleitung bestätigt: ${eventTitle}`
      : "Begleitung bestätigt";
  }

  // ausplanung_* covers five flavours, distinguished by payload.kind +
  // payload.absence_type. Same mapping as NotificationHistoryPanel +
  // NotificationDetailDialog.
  if (eventType === "ausplanung_rejected" || eventType === "ausplanung_approved") {
    const isApproved = eventType === "ausplanung_approved";
    const kind =
      payload && typeof payload === "object"
        ? (payload as Record<string, unknown>).kind
        : undefined;
    const absenceType =
      payload && typeof payload === "object"
        ? (payload as Record<string, unknown>).absence_type
        : undefined;
    let subject = "Ausplanung";
    if (kind === "class_trip") {
      if (absenceType === "ausflug") subject = "Ausflug";
      else if (absenceType === "klassenfahrt") subject = "Klassenfahrt";
      else subject = "Klassenabwesenheit";
    } else if (absenceType === "fortbildung") {
      subject = "Fortbildung";
    }
    const verb = isApproved
      ? `${subject} genehmigt`
      : (subject === "Ausplanung" ? "Ausplanung nicht möglich" : `${subject} abgelehnt`);
    return typeof eventTitle === "string" && eventTitle ? `${verb}: ${eventTitle}` : verb;
  }
  if (eventType?.startsWith("umfrage.") || eventType?.startsWith("bulletin.")) {
    return typeof mitteilungTitle === "string" && mitteilungTitle
      ? mitteilungTitle
      : "Mitteilung";
  }
  if (eventType === "distribution.published")
    return "Schüler:innen zu deiner Klasse zugeteilt";
  if (eventType === "distribution.withdrawn")
    return "Verteilung zurückgezogen";
  if (eventType === "distribution.stay_home") return "Stunde entfällt";
  return isUpdate ? "Vertretungsplan aktualisiert" : "Neuer Vertretungsplan";
}

// ─── Hook ────────────────────────────────────────────────────────────────────
export function useNotificationListener() {
  const { profileId, schoolId } = useAuth() as {
    profileId: string | null;
    schoolId: string | null;
  };

  const dispatch = useDispatch();
  // Last notification payload for toast display
  const lastPayloadRef = useRef<PushPayload | null>(null);
  // ─── Toast dedupe (separate from cache — cache must never be blocked) ───────
  const toastDedupeRef = useRef<Map<string, number>>(new Map());

  const isToastDuplicate = useCallback(
    (eventType: string, eventKey: string) => {
      const key = `${eventType}|${eventKey}`;
      const now = Date.now();
      const prev = toastDedupeRef.current.get(key);
      if (prev && now - prev < DEDUPE_TTL_MS) return true;
      toastDedupeRef.current.set(key, now);
      // Housekeep: remove expired entries
      for (const [k, t] of toastDedupeRef.current) {
        if (now - t > DEDUPE_TTL_MS) toastDedupeRef.current.delete(k);
      }
      return false;
    },
    [],
  );

  // ─── Build toast URL with optional queue_id ────────────────────────────────
  const buildToastUrl = (row: Record<string, unknown>) => {
    const base = `/dashboard/teacher?date=${row.event_date as string}`;
    return row.queue_id ? `${base}&queue_id=${row.queue_id}` : base;
  };

  // ─── Emit toast only (no cache mutation) — used for "held" rows ────────────
  const emitToastOnly = useCallback(
    (row: Record<string, unknown>) => {
      const eventType = row.event_type as string;
      const eventKey = row.event_key as string;
      if (!eventType || !eventKey) return;
      if (isToastDuplicate(eventType, eventKey)) return;

      const isUpdate = (row.is_update as boolean) ?? false;
      const payload =
        (row.payload as Record<string, unknown> | null | undefined) ?? null;
      lastPayloadRef.current = {
        type: "notification",
        event_type: eventType,
        event_key: eventKey,
        url: buildToastUrl(row),
        title: toastTitle(eventType, isUpdate, payload),
        body: "Tippen, um Details zu öffnen.",
      };
      window.dispatchEvent(
        new CustomEvent("fw:notification", { detail: lastPayloadRef.current }),
      );
    },
    [isToastDuplicate],
  );

  // ─── Patch RTK cache from CDC event ────────────────────────────────────────
  // Cache patch is NEVER blocked by dedupe. Toast dedupe is separate.
  const patchCacheWithDelivery = useCallback(
    (
      row: Record<string, unknown>,
      opts: { emitToast: boolean } = { emitToast: true },
    ) => {
      const eventType = row.event_type as string;
      const eventKey = row.event_key as string;
      if (!eventType || !eventKey) return;

      const item: NotificationHistoryItem = {
        id: row.id as string,
        event_type: eventType,
        event_key: eventKey,
        event_date: row.event_date as string,
        status: row.status as string,
        sent_at: row.sent_at as string,
        read_at: (row.read_at as string) ?? null,
        is_update: (row.is_update as boolean) ?? false,
        queue_id: (row.queue_id as string) ?? null,
        payload: (row.payload as Record<string, unknown>) ?? null,
      };

      // Always patch RTK cache (no dedupe blocking)
      dispatch(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (notificationsApi.util.updateQueryData as any)(
          "getMyNotificationHistory",
          undefined,
          (draft: NotificationHistoryPayload) => {
            // Check if this logical notification already exists in cache
            const existingIdx = draft.items.findIndex(
              (i) =>
                i.event_type === item.event_type &&
                i.event_key === item.event_key,
            );

            if (existingIdx >= 0) {
              const oldItem = draft.items[existingIdx];
              const wasUnread = !oldItem.read_at;
              const isNowUnread = !item.read_at;

              // Adjust unread_count based on transition
              if (wasUnread && !isNowUnread) {
                draft.unread_count = Math.max(0, draft.unread_count - 1);
              } else if (!wasUnread && isNowUnread) {
                draft.unread_count += 1;
              }

              // Replace the entry
              draft.items[existingIdx] = item;
            } else {
              // New delivery — prepend and trim to 20
              draft.items.unshift(item);
              if (draft.items.length > 20) draft.items.pop();
              if (!item.read_at) {
                draft.unread_count += 1;
              }
            }
          },
        ),
      );

      // Toast — only when requested + toast dedupe
      if (opts.emitToast && !isToastDuplicate(eventType, eventKey)) {
        lastPayloadRef.current = {
          type: "notification",
          event_type: eventType,
          event_key: eventKey,
          url: buildToastUrl(row),
          title: toastTitle(eventType, item.is_update, item.payload),
          body: "Tippen, um Details zu öffnen.",
        };
        window.dispatchEvent(
          new CustomEvent("fw:notification", {
            detail: lastPayloadRef.current,
          }),
        );
      }
    },
    [dispatch, isToastDuplicate],
  );

  // ─── Channel 1: SW postMessage ─────────────────────────────────────────────
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handler = (event: MessageEvent) => {
      const msg = event.data as SWMessage;

      if (msg?.type === "NOTIFICATION_RECEIVED") {
        // SW postMessage toast uses toastDedupe to prevent double with CDC
        if (
          msg.payload?.event_type &&
          msg.payload?.event_key &&
          !isToastDuplicate(msg.payload.event_type, msg.payload.event_key)
        ) {
          lastPayloadRef.current = msg.payload;
          window.dispatchEvent(
            new CustomEvent("fw:notification", { detail: msg.payload }),
          );
        }
      }

      if (msg?.type === "NOTIFICATION_CLICKED") {
        lastPayloadRef.current = {
          type: "notification",
          event_type: "click",
          event_key: "",
          url: msg.url,
          title: "",
          body: "",
        };
      }
    };

    navigator.serviceWorker.addEventListener("message", handler);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handler);
      lastPayloadRef.current = null;
    };
  }, [isToastDuplicate]);

  // ─── Channels 2 & 3: notification CDC + visibility-aware lifecycle ────────
  // Both subscriptions share an effect so the visibility teardown manages
  // them as a single unit (matching the pattern in useSchoolRealtime —
  // see PR #503). Without this, a tab backgrounded for hours holds 2
  // realtime.subscription rows on the server and floods the realtime pod
  // with InvalidJWTToken errors when its access_token expires.
  useEffect(() => {
    if (!profileId || !schoolId) return;

    let activeChannels: Array<ReturnType<typeof supabase.channel>> = [];
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;
    let setupInProgress = false;
    // Set when we tear down due to hidden state; on next subscribe we
    // invalidate the NotificationHistory cache so the bell refetches
    // anything that landed while we were disconnected.
    let wokeUp = false;

    // Hidden-tab grace before tearing down — same constant as #503.
    const HIDE_TEARDOWN_DELAY_MS = 60_000;

    const teardown = () => {
      activeChannels.forEach((ch) => {
        try { supabase.removeChannel(ch); } catch { /* no-op */ }
      });
      activeChannels = [];
    };

    const setupRealtime = async () => {
      if (cancelled || setupInProgress || activeChannels.length > 0) return;
      setupInProgress = true;
      try {
        // Channel 2: notification_deliveries CDC
        // Single `.on("postgres_changes", { event: "*", ... })` instead of
        // one per event — the duplicate-filter cleanup from #476.
        const deliveriesChannel = supabase
          .channel(`deliveries:${profileId}`)
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "notification_deliveries",
              filter: `user_profile_id=eq.${profileId}`,
            },
            (payload) => {
              if (cancelled) return;
              if (payload.eventType === "INSERT") {
                const row = payload.new as Record<string, unknown>;
                const showInBell =
                  row.status === "sent" || row.status === "skipped_pref";
                if (showInBell) {
                  patchCacheWithDelivery(row); // cache + toast
                } else if (row.status === "held") {
                  emitToastOnly(row); // toast only, no cache/unread
                }
                return;
              }

              if (payload.eventType === "UPDATE") {
                const oldRow = payload.old as Record<string, unknown>;
                const newRow = payload.new as Record<string, unknown>;
                const isBell = (s: unknown) =>
                  s === "sent" || s === "skipped_pref";
                if (isBell(oldRow.status) || !isBell(newRow.status)) return;
                patchCacheWithDelivery(newRow, { emitToast: false });
              }
            },
          );

        // Channel 3 (REMOVED): used to subscribe to notification_send_queue
        // and fire a "Vertretungsplan aktualisiert" toast for every send
        // that completed in the user's school — including the admin who
        // initiated the send, even though they are not a recipient.
        //
        // Per-recipient toasts are already handled by Channel 2
        // (notification_deliveries CDC, filtered by user_profile_id), so
        // an actual recipient still gets exactly one toast. The admin
        // banner state on the planner is independently kept in sync by
        // useQueueRealtimeSync, so removing this channel does not affect
        // the banner countdown / delivery-log refresh.
        const queueChannel: ReturnType<typeof supabase.channel> | null = null;

        // Subscribe. The deliveries channel triggers wake refetch on its
        // SUBSCRIBED status; nothing else to subscribe — the removed
        // queue-toast channel is intentionally gone.
        deliveriesChannel.subscribe((status) => {
          if (cancelled) return;
          if (status === "SUBSCRIBED" && wokeUp) {
            wokeUp = false;
            // Re-fetch the bell history so we catch up on anything that
            // landed during the hidden window. RTK Query's tag invalidation
            // wakes any active query subscriptions.
            dispatch(
              notificationsApi.util.invalidateTags(["NotificationHistory"]),
            );
          }
        });
        // Reference queueChannel to keep the linter quiet — variable is
        // retained as documentation of what the third channel used to be.
        void queueChannel;

        activeChannels.push(deliveriesChannel);
      } finally {
        setupInProgress = false;
      }
    };

    const onVisibility = () => {
      if (typeof document === "undefined") return;
      const nowVisible = document.visibilityState !== "hidden";
      if (!nowVisible) {
        if (hideTimer) clearTimeout(hideTimer);
        hideTimer = setTimeout(() => {
          if (
            !cancelled &&
            typeof document !== "undefined" &&
            document.visibilityState === "hidden" &&
            activeChannels.length > 0
          ) {
            teardown();
          }
        }, HIDE_TEARDOWN_DELAY_MS);
      } else {
        if (hideTimer) {
          clearTimeout(hideTimer);
          hideTimer = null;
        }
        if (activeChannels.length === 0 && !cancelled) {
          wokeUp = true;
          void setupRealtime();
        }
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibility);
    }

    void setupRealtime();

    // Evaluate the initial visibility state so a tab that mounts while
    // already hidden (background-tab open, app restored on next session)
    // schedules its teardown timer. Without this, the visibilitychange
    // listener only reacts to future transitions and the channels would
    // stay subscribed forever in a always-hidden tab.
    onVisibility();

    return () => {
      cancelled = true;
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibility);
      }
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
      teardown();
    };
  }, [profileId, schoolId, patchCacheWithDelivery, emitToastOnly, dispatch]);

  // ─── Return ────────────────────────────────────────────────────────────────
  return {
    lastPayloadRef,
  };
}
